import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
import type { MembershipInfo } from '../services/authService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Multi-tenant: client selection state
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [memberships, setMemberships] = useState<MembershipInfo[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      setError(t('pages.login.errorMissingFields'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await login(username, password);

      if (result.success) {
        // Check if client selection is required (multi-tenant)
        if (result.requires_client_selection && result.memberships && result.memberships.length > 1) {
          setMemberships(result.memberships);
          setShowClientSelector(true);
          setLoading(false);
          return;
        }
        
        const returnUrl = new URLSearchParams(window.location.search).get('return') || '/';
        navigate(decodeURIComponent(returnUrl));
      } else {
        setError(result.message || t('pages.login.errorInvalidCredentials'));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = error.response?.data?.message || error.message || t('pages.login.errorInvalidCredentials');
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClientId) {
      setError(t('pages.login.errorSelectClient'));
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const result = await authService.selectClient(selectedClientId);
      
      if (result.success) {
        const returnUrl = new URLSearchParams(window.location.search).get('return') || '/';
        navigate(decodeURIComponent(returnUrl));
      } else {
        setError(result.message || t('errors.unknown'));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = error.response?.data?.detail || error.message || t('errors.unknown');
      setError(errorMessage);
      console.error('Client selection error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Client selector view
  if (showClientSelector) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[image:url('/login-bg.jpg')] bg-center bg-cover bg-[rgba(255,255,255,0.82)] bg-blend-overlay">
        <div className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 mx-4">
          <div className="text-center mb-6">
            <span className="inline-block px-4 py-1.5 bg-primary-600 text-white text-sm font-semibold rounded-md mb-3">
              {t('app.title')}
            </span>
            <h1 className="text-xl font-semibold text-slate-900">{t('pages.login.selectClient')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('pages.login.selectClientPrompt')}</p>
          </div>

          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleClientSelect} className="space-y-4">
            <div className="space-y-2">
              {memberships.map((membership) => (
                <label
                  key={membership.client_id}
                  className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    selectedClientId === membership.client_id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="client"
                    value={membership.client_id}
                    checked={selectedClientId === membership.client_id}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="accent-primary-600"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{membership.client_name}</p>
                    <p className="text-xs text-slate-500">{t('pages.users.role')}: {membership.role}</p>
                  </div>
                </label>
              ))}
            </div>

            <Button type="submit" className="w-full" disabled={loading || !selectedClientId}>
              {loading ? t('pages.login.selectingClient') : t('common.confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowClientSelector(false);
                setMemberships([]);
                authService.logout();
              }}
            >
              {t('common.back')}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[image:url('/login-bg.jpg')] bg-center bg-cover bg-[rgba(255,255,255,0.82)] bg-blend-overlay">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 mx-4">
        <div className="text-center mb-6">
          <span className="inline-block px-4 py-1.5 bg-primary-600 text-white text-sm font-semibold rounded-md mb-3">
            {t('app.title')}
          </span>
          <h1 className="text-xl font-semibold text-slate-900">{t('pages.login.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('pages.login.subtitle')}</p>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username">{t('pages.login.username')}</Label>
            <Input
              type="text"
              id="username"
              placeholder={t('pages.login.usernamePlaceholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onInput={() => setError('')}
              required
              autoComplete="username"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">{t('pages.login.password')}</Label>
            <Input
              type="password"
              id="password"
              placeholder={t('pages.login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onInput={() => setError('')}
              required
              autoComplete="current-password"
            />
          </div>

          <Button type="submit" className="w-full mt-2" disabled={loading}>
            {loading ? t('pages.login.loggingIn') : t('pages.login.loginButton')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
